// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { AddressInfoDisplayStyles } from "@insite/content-library/Components/AddressInfoDisplay";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { IconPresentationProps, IconWrapper } from "@insite/mobius/Icon";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import SignOut from "@stanion/XDL/Icons/SignOut";
import { color, xScreen } from "@stanion/XDL/Utilities";
import { css } from "styled-components";

export interface MiniMyAccountStylesInterface {
    spinnerWrapper?: InjectableCss;
    wrapper?: InjectableCss;
    topSection?: InjectableCss;
    title?: TypographyPresentationProps;
    customerInformation?: InjectableCss;
    addressStyles: AddressInfoDisplayStyles;
    changeCustomerButton?: ButtonPresentationProps;
    contentWrapper?: InjectableCss;
    linkItems?: {
        wrapper?: InjectableCss;
        linkWrapper?: InjectableCss;
        title?: TypographyPresentationProps;
        link?: LinkPresentationProps;
    };
    profileInfo?: {
        wrapper?: InjectableCss;
        section?: InjectableCss;
        title?: TypographyPresentationProps;
        link?: ClickablePresentationProps;
        icon?: IconPresentationProps;
    };
    signOutButton?: ButtonPresentationProps;
}

export const miniMyAccountStyles: MiniMyAccountStylesInterface = {
    spinnerWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;
            height: 100vh;
        `,
    },
    wrapper: {
        css: css``,
    },
    topSection: {
        css: css`
            background-color: ${color("common.ui8", 0.1)};
            color: ${color("text.main")};
            width: 100%;
            padding: 15px;
        `,
    },
    title: {
        variant: "h4",
        css: css`
            margin-bottom: 0;
            ${xScreen("lte", "md")} {
                color: ${color("text.link")};
            }

            span {
                font-size: 22px;
                color: ${color("primary.main")};
            }
        `,
    },
    customerInformation: {
        css: css`
            margin-top: 10px;
            & > div {
                margin-bottom: 5px;
            }
        `,
    },
    changeCustomerButton: {
        mergeCss: true,
        variant: "secondary",
        css: css`
            margin-top: 15px;
        `,
    },
    addressStyles: {
        wrapper: {
            css: css`
                display: inline;
                margin-left: 5px;
            `,
        },
        customerNameText: { as: "span" },
        attentionText: { as: "span" },
        companyNameText: { as: "span" },
        address1Text: { as: "span" },
        address2Text: { as: "span" },
        address3Text: { as: "span" },
        address4Text: { as: "span" },
        cityStatePostalCodeText: { as: "span" },
        countryText: { as: "span" },
        phoneText: { as: "span" },
        emailText: { as: "span" },
        faxText: { as: "span" },
    },
    contentWrapper: {
        css: css`
            padding: 15px;
            width: 100%;
        `,
    },
    linkItems: {
        wrapper: {
            css: css`
                display: flex;
                flex-wrap: wrap;
                margin-bottom: 24px;
                gap: 12px 10px;
                ${xScreen("lt", "sm")} {
                    flex-direction: column;
                }
            `,
        },
        title: {
            variant: "h6",
            transform: "capitalize",
            css: css`
                flex-basis: 100%;
                border-bottom: 1px solid ${color("common.border")};
                margin-bottom: 0;
                line-height: 30px;
            `,
        },
        linkWrapper: {
            css: css`
                flex-basis: calc(50% - 5px);
            `,
        },
        link: {
            color: "primary.main",
            typographyProps: {
                size: 14,
                weight: "bold",
                css: css`
                    letter-spacing: 0;
                    line-height: 22px;
                `,
            },
        },
    },
    profileInfo: {
        wrapper: {
            css: css`
                display: flex;
                gap: 5px 5px;
                margin-top: 20px;
            `,
        },
        section: {
            css: css`
                flex-basis: calc(50% - 5px);
            `,
        },
        title: {
            size: 14,
            weight: "bold",
            transform: "capitalize",
            ellipsis: true,
            css: css`
                margin-bottom: 8px;
                line-height: 19px;
                display: block;
            `,
        },
        link: {
            css: css`
                color: ${color("text.main")};
                font-size: 14px;
                letter-spacing: 0;
                line-height: 22px;
                margin-bottom: 10px;
                &:hover {
                    color: ${color("text.link")};
                }
            `,
        },
        icon: {
            css: css`
                color: ${color("common.ui8")};
                height: 15px;
                width: 15px;
                margin-right: 8px;
            `,
        },
    },
    signOutButton: {
        mergeCss: true,
        variant: "tertiary",
        css: css`
            padding: 0;
            margin-top: 20px;
            ${IconWrapper} {
                &, svg {
                    color: ${color("primary")};
                    width: 14px;
                    height: 14px;
                }
            }

            &:hover {
                svg {
                    color: ${color("common.hover")};
                }
            }
        `,
        typographyProps: {
            size: 14,
            css: css`
                margin-top: 5px;
                margin-right: 3px;
            `,
        },
        icon: {
            src: SignOut,
            position: "right",
        }
    },
};
